import { useEffect, useRef, useState } from 'react';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { bgToCss } from './storyUtils';
import { loadGoogleFont } from '../../lib/googleFonts';

const EDITOR_W = 412;

function hexToRgba(hex, opacity) {
  if (!hex?.startsWith('#')) return hex || 'transparent';
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}

function SvgWrap({ viewBox, children, fill, stroke, strokeWidth }) {
  return (
    <svg viewBox={viewBox} style={{ width: '100%', height: '100%' }} fill={fill} stroke={stroke || 'none'} strokeWidth={strokeWidth}>
      {children}
    </svg>
  );
}

function ShapePreview({ data }) {
  const f = data.fill;
  const s = data.stroke || 'none';
  const sw = data.strokeWidth || 0;
  const cssBase = { width: '100%', height: '100%', background: f, border: data.stroke ? `${sw}px solid ${s}` : 'none' };

  switch (data.shape) {
    case 'circle':
    case 'ellipse':
      return <div style={{ ...cssBase, borderRadius: '50%' }} />;
    case 'rounded-rectangle':
      return <div style={{ ...cssBase, borderRadius: data.borderRadius || 24 }} />;
    case 'pill':
      return <div style={{ ...cssBase, borderRadius: 999 }} />;
    case 'line':
      return <div style={{ ...cssBase, borderRadius: 2 }} />;
    case 'line-diagonal':
      return <SvgWrap viewBox="0 0 100 100" fill="none" stroke={f} strokeWidth={4}><line x1="5" y1="95" x2="95" y2="5" strokeLinecap="round" /></SvgWrap>;
    case 'triangle':
      return <SvgWrap viewBox="0 0 100 86" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,0 100,86 0,86" /></SvgWrap>;
    case 'triangle-down':
      return <SvgWrap viewBox="0 0 100 86" fill={f} stroke={s} strokeWidth={sw}><polygon points="0,0 100,0 50,86" /></SvgWrap>;
    case 'diamond':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,5 95,50 50,95 5,50" /></SvgWrap>;
    case 'star':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,5 61,35 95,35 68,57 79,91 50,70 21,91 32,57 5,35 39,35" /></SvgWrap>;
    case 'star6':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,5 59,35 88,20 73,47 95,65 63,65 50,95 37,65 5,65 27,47 12,20 41,35" /></SvgWrap>;
    case 'heart':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M50,88 C50,88 12,60 12,35 C12,22 22,15 35,15 C42,15 48,19 50,23 C52,19 58,15 65,15 C78,15 88,22 88,35 C88,60 50,88 50,88Z" /></SvgWrap>;
    case 'pentagon':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,5 97,38 79,95 21,95 3,38" /></SvgWrap>;
    case 'hexagon':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,5 93,27 93,73 50,95 7,73 7,27" /></SvgWrap>;
    case 'octagon':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="30,5 70,5 95,30 95,70 70,95 30,95 5,70 5,30" /></SvgWrap>;
    case 'arrow-right':
      return <SvgWrap viewBox="0 0 100 60" fill={f} stroke={s} strokeWidth={sw}><polygon points="0,15 65,15 65,0 100,30 65,60 65,45 0,45" /></SvgWrap>;
    case 'arrow-left':
      return <SvgWrap viewBox="0 0 100 60" fill={f} stroke={s} strokeWidth={sw}><polygon points="100,15 35,15 35,0 0,30 35,60 35,45 100,45" /></SvgWrap>;
    case 'arrow-up':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,5 90,55 70,55 70,95 30,95 30,55 10,55" /></SvgWrap>;
    case 'arrow-down':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,95 90,45 70,45 70,5 30,5 30,45 10,45" /></SvgWrap>;
    case 'chevron-right':
      return <SvgWrap viewBox="0 0 100 100" fill="none"><polyline points="30,10 70,50 30,90" fill="none" stroke={f} strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" /></SvgWrap>;
    case 'chevron-left':
      return <SvgWrap viewBox="0 0 100 100" fill="none"><polyline points="70,10 30,50 70,90" fill="none" stroke={f} strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" /></SvgWrap>;
    case 'cross':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="35,0 65,0 65,35 100,35 100,65 65,65 65,100 35,100 35,65 0,65 0,35 35,35" /></SvgWrap>;
    case 'parallelogram':
      return <SvgWrap viewBox="0 0 100 60" fill={f} stroke={s} strokeWidth={sw}><polygon points="20,0 100,0 80,60 0,60" /></SvgWrap>;
    case 'trapezoid':
      return <SvgWrap viewBox="0 0 100 60" fill={f} stroke={s} strokeWidth={sw}><polygon points="20,0 80,0 100,60 0,60" /></SvgWrap>;
    case 'ring':
      return <SvgWrap viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="38" fill="none" stroke={f} strokeWidth="10" /></SvgWrap>;
    case 'half-circle':
      return <SvgWrap viewBox="0 0 100 55" fill={f} stroke={s} strokeWidth={sw}><path d="M5,50a45,45,0,0,1,90,0z" /></SvgWrap>;
    case 'quarter-circle':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M5,95a90,90,0,0,1,90-90v90z" /></SvgWrap>;
    case 'wave':
      return <SvgWrap viewBox="0 0 100 60" fill="none"><path d="M0,30 C15,5 35,55 50,30 C65,5 85,55 100,30" fill="none" stroke={f} strokeWidth="8" strokeLinecap="round" /></SvgWrap>;
    case 'zigzag':
      return <SvgWrap viewBox="0 0 100 60" fill="none"><polyline points="5,50 20,10 35,50 50,10 65,50 80,10 95,50" fill="none" stroke={f} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" /></SvgWrap>;
    case 'badge':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><circle cx="50" cy="45" r="35" /><polygon points="32,72 50,95 68,72" /></SvgWrap>;
    case 'burst':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,2 58,30 88,12 70,38 98,50 70,62 88,88 58,70 50,98 42,70 12,88 30,62 2,50 30,38 12,12 42,30" /></SvgWrap>;
    case 'chat-bubble':
      return <SvgWrap viewBox="0 0 100 90" fill={f} stroke={s} strokeWidth={sw}><path d="M8,8 h84 a8,8 0 0 1 8,8 v42 a8,8 0 0 1 -8,8 H35 l-15,20 v-20 H8 a8,8 0 0 1 -8,-8 V16 a8,8 0 0 1 8,-8z" /></SvgWrap>;
    case 'speech-bubble':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M50,5C24,5,5,20,5,40c0,12,8,22,20,28l-5,22l20-15c3,0.5,6.5,0.8,10,0.8c26,0,45-15,45-35S76,5,50,5z" /></SvgWrap>;
    case 'flame':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M50,5 C50,5 75,30 75,55 C75,72 64,85 50,90 C36,85 25,72 25,55 C25,45 35,35 40,30 C40,30 38,50 50,55 C50,55 50,30 50,5Z" /></SvgWrap>;
    case 'lightning':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="55,5 25,50 45,50 40,95 75,42 52,42" /></SvgWrap>;
    case 'location-pin':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M50,90 C50,90 18,55 18,35 C18,17 32,5 50,5 S82,17 82,35 C82,55 50,90 50,90Z M50,24 a12,12 0 1,0 0,24 a12,12 0 1,0 0,-24Z" fillRule="evenodd" /></SvgWrap>;
    case 'crown':
      return <SvgWrap viewBox="0 0 100 80" fill={f} stroke={s} strokeWidth={sw}><polygon points="10,70 10,25 30,42 50,8 70,42 90,25 90,70" /></SvgWrap>;
    case 'checkmark':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><circle cx="50" cy="50" r="45" opacity="0.2" /><polyline points="28,50 44,66 74,36" fill="none" stroke={f} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" /></SvgWrap>;
    case 'x-mark':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><circle cx="50" cy="50" r="45" opacity="0.2" /><line x1="33" y1="33" x2="67" y2="67" stroke={f} strokeWidth="10" strokeLinecap="round" /><line x1="67" y1="33" x2="33" y2="67" stroke={f} strokeWidth="10" strokeLinecap="round" /></SvgWrap>;
    case 'music-note':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M70,10v55c0,10-9,18-20,18S30,75,30,65s9-15,20-15c4,0,7,1,10,3V25L40,30v-15L70,10z" /></SvgWrap>;
    case 'blob':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M45,10 C65,5 85,15 90,35 C95,55 85,75 70,85 C55,95 35,90 20,78 C5,66 5,45 15,30 C25,15 35,12 45,10Z" /></SvgWrap>;
    case 'scalloped-circle':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M50,8 C55,15 62,10 67,15 C72,20 68,28 73,33 C78,38 87,36 90,43 C93,50 86,55 87,62 C88,69 96,74 95,81 C94,88 85,89 81,95 C77,95 72,90 65,91 C58,92 55,95 50,95 C45,95 42,92 35,91 C28,90 23,95 19,95 C15,89 6,88 5,81 C4,74 12,69 13,62 C14,55 7,50 10,43 C13,36 22,38 27,33 C32,28 28,20 33,15 C38,10 45,15 50,8Z" /></SvgWrap>;
    case 'instagram':
      return <SvgWrap viewBox="0 0 100 100" fill="none"><rect x="15" y="15" width="70" height="70" rx="20" fill="none" stroke={f} strokeWidth="7" /><circle cx="50" cy="50" r="18" fill="none" stroke={f} strokeWidth="7" /><circle cx="73" cy="27" r="5" fill={f} /></SvgWrap>;
    case 'facebook':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M80,5H20C11.7,5,5,11.7,5,20v60c0,8.3,6.7,15,15,15h25V62H35V48h10V38c0-11,7.2-17,16.5-17c4.7,0,9.5,0.8,9.5,0.8v10.7H65.7c-5.3,0-7,3.3-7,6.7V48h12l-2,14H58.7v33H80c8.3,0,15-6.7,15-15V20C95,11.7,88.3,5,80,5z" /></SvgWrap>;
    case 'twitter-x':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M87,10L53,52l36,38H78L51,59L29,90H5l36-42L7,10h12l24,27L64,10H87zM75,83L22,17h6l53,66H75z" /></SvgWrap>;
    case 'youtube':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><rect x="5" y="18" width="90" height="64" rx="16" /><polygon points="40,35 40,65 68,50" fill="#fff" /></SvgWrap>;
    case 'tiktok':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M72,5v20c10,2,18-3,22-8v18c-6,3-14,2-22-3v30c0,18-15,33-33,33S6,80,6,62s15-33,33-33v18c-8,0-15,7-15,15s7,15,15,15s15-7,15-15V5H72z" /></SvgWrap>;
    case 'linkedin':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><rect x="5" y="5" width="90" height="90" rx="15" /><rect x="18" y="42" width="14" height="40" fill="#fff" /><circle cx="25" cy="28" r="9" fill="#fff" /><path d="M44,42h13v5.5c2-3.5,7-6.5,14-6.5c13,0,16,8,16,20v21H74V64c0-5-1-10-8-10c-7,0-9,5-9,10v18H44V42z" fill="#fff" /></SvgWrap>;
    case 'whatsapp':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M50,5C25,5,5,25,5,50c0,8,2.2,15.5,5.8,22L5,95l23.5-5.5C35,92.5,42.3,95,50,95c25,0,45-20,45-45S75,5,50,5zM50,85c-7,0-13.5-2-19-5.5l-13,3.5l3.5-12.5C18,65,16,57.7,16,50c0-18.8,15.2-34,34-34s34,15.2,34,34S68.8,85,50,85z" /></SvgWrap>;
    case 'snapchat':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M50,8c12,0,22,10,24,25c1,5,0,10,0,15c5,2,12,3,15,6c-5,3-12,4-14,8c-2,5,5,18-8,25c-8,4-12,3-17,5c-5-2-9-1-17-5c-13-7-6-20-8-25c-2-4-9-5-14-8c3-3,10-4,15-6c0-5-1-10,0-15C28,18,38,8,50,8z" /></SvgWrap>;
    case 'spotify':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><circle cx="50" cy="50" r="45" /><path d="M30,38 C45,33 65,33 78,40" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round" /><path d="M33,52 C45,48 62,48 73,53" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" /><path d="M37,65 C47,62 58,62 68,66" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" /></SvgWrap>;
    case 'telegram':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><circle cx="50" cy="50" r="45" /><path d="M25,50 L72,32 L60,75 L47,58 L25,50Z" fill="#fff" /><path d="M47,58 L50,72 L55,58" fill="#d2e4f0" /></SvgWrap>;
    case 'shopping-bag':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><rect x="18" y="35" width="64" height="55" rx="5" /><path d="M33,40 V25 C33,15 40,8 50,8 S67,15 67,25 V40" fill="none" stroke={f} strokeWidth="6" strokeLinecap="round" /></SvgWrap>;
    case 'percent-badge':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><circle cx="50" cy="50" r="42" /><circle cx="38" cy="38" r="8" fill="none" stroke="#fff" strokeWidth="4" /><circle cx="62" cy="62" r="8" fill="none" stroke="#fff" strokeWidth="4" /><line x1="65" y1="33" x2="35" y2="67" stroke="#fff" strokeWidth="4" strokeLinecap="round" /></SvgWrap>;
    case 'price-sticker':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><polygon points="50,5 58,20 75,12 70,30 90,32 78,45 92,58 75,60 78,78 60,70 52,88 42,72 25,80 28,62 8,58 22,45 10,32 28,30 25,12 42,20" /><text x="50" y="55" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="bold">$</text></SvgWrap>;
    case 'pinterest':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M50,5C25,5,5,25,5,50c0,19,11.5,35.2,28,42c-0.5-3.5-0.8-8.8,0-12.5c1-4,6-25.5,6-25.5s-1.5-3-1.5-7.5c0-7,4-12.2,9-12.2c4.3,0,6.3,3.2,6.3,7c0,4.3-2.7,10.7-4.2,16.5c-1.2,5,2.5,9.2,7.5,9.2c9,0,16-9.5,16-23c0-12-8.7-20.5-21-20.5c-14.3,0-22.7,10.7-22.7,21.8c0,4.3,1.7,9,3.7,11.5c0.5,0.5,0.5,1,0.3,1.5c-0.3,1.5-1.2,5-1.3,5.5c-0.2,0.8-0.8,1-1.5,0.5C23,60,18.7,51.3,18.7,43c0-16,11.5-30.5,33.3-30.5c17.5,0,31,12.5,31,29C83,60,72,74,57,74c-5,0-9.7-2.5-11.3-5.5l-3.2,12c-1.2,4.5-4.2,10-6.3,13.3C40,95.2,45,96,50,96c25,0,45-20,45-45C95,26,75,5,50,5z" /></SvgWrap>;
    case 'tag':
      return <SvgWrap viewBox="0 0 100 100" fill={f} stroke={s} strokeWidth={sw}><path d="M10,10h45l35,40l-35,40H10V10z" /></SvgWrap>;
    case 'ribbon':
      return <SvgWrap viewBox="0 0 100 80" fill={f} stroke={s} strokeWidth={sw}><path d="M10,20h80v50c0,0-20-10-40,0s-40,0-40,0V20z M10,20l15,10L10,40 M90,20l-15,10L90,40" /></SvgWrap>;
    case 'brace':
      return <SvgWrap viewBox="0 0 100 100" fill="none"><path d="M35,5 C25,5 20,15 20,25 L20,40 C20,45 15,50 10,50 C15,50 20,55 20,60 L20,75 C20,85 25,95 35,95 M65,5 C75,5 80,15 80,25 L80,40 C80,45 85,50 90,50 C85,50 80,55 80,60 L80,75 C80,85 75,95 65,95" fill="none" stroke={f} strokeWidth="6" strokeLinecap="round" /></SvgWrap>;
    case 'swipe-up':
      return <SvgWrap viewBox="0 0 100 100" fill="none"><line x1="50" y1="85" x2="50" y2="30" stroke={f} strokeWidth="6" strokeLinecap="round" /><polyline points="30,50 50,25 70,50" fill="none" stroke={f} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" /><circle cx="50" cy="15" r="6" fill={f} /></SvgWrap>;
    default:
      return <div style={{ ...cssBase, borderRadius: `${data.borderRadius || 0}px` }} />;
  }
}

function FormPreview({ data, scale: sc }) {
  const fs = data.style ?? {};
  const bgOpacity = (fs.backgroundOpacity ?? 90) / 100;
  const formBg = hexToRgba(fs.backgroundColor || '#0a0a19', bgOpacity);
  const formText = fs.textColor || '#ffffff';
  const accent = fs.accentColor || 'var(--brand-primary)';
  const btnColor = fs.buttonColor || 'var(--brand-primary)';
  const br = Math.max(8, (fs.borderRadius ?? 12) * sc);
  const visibleFields = (data.fields || []).filter(f => f.type !== 'hidden');

  // s() enforces a minimum readable pixel size at any scale
  const s = (base, min = 8) => Math.max(min, base * sc);

  const labelStyle = {
    fontSize: `${s(8, 8)}px`,
    color: `${formText}cc`,
    fontWeight: 600,
    marginBottom: `${s(2, 2)}px`,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  const inputBox = {
    background: 'rgba(255,255,255,0.13)',
    border: '1px solid rgba(255,255,255,0.30)',
    borderRadius: `${s(4, 4)}px`,
    padding: `0 ${s(6, 6)}px`,
    height: `${s(14, 16)}px`,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  };
  const phStyle = { fontSize: `${s(7, 7)}px`, color: `${formText}55`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <div style={{
      width: '100%', height: '100%',
      background: formBg,
      borderRadius: br,
      padding: `${s(8, 10)}px ${s(10, 12)}px`,
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid rgba(255,255,255,0.15)',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: `${s(4, 4)}px`, flexShrink: 0, marginBottom: `${s(5, 6)}px` }}>
        <span style={{
          background: accent, color: '#fff',
          fontSize: `${s(7, 7)}px`, fontWeight: 700,
          padding: `${s(2, 2)}px ${s(5, 5)}px`,
          borderRadius: `${s(3, 3)}px`, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
        }}>FORM</span>
        {data.formTitle && (
          <span style={{
            fontSize: `${s(9, 9)}px`, fontWeight: 700, color: formText,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>
            {data.formTitle}
          </span>
        )}
      </div>

      {/* Fields — fill height evenly */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        justifyContent: 'space-evenly', overflow: 'hidden', minHeight: 0,
      }}>
        {visibleFields.map((field, fi) => {
          if (field.type === 'submit') {
            return (
              <div key={fi}>
                <div style={{
                  background: btnColor,
                  borderRadius: 999,
                  height: `${s(16, 18)}px`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: `${s(8, 8)}px`, fontWeight: 700, color: '#fff', letterSpacing: '0.03em' }}>
                    {field.label || 'Submit'}
                  </span>
                </div>
              </div>
            );
          }

          if (field.type === 'radio') {
            const opts = (field.options || []).slice(0, 4);
            return (
              <div key={fi}>
                <div style={labelStyle}>{field.label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: `${s(3, 3)}px` }}>
                  {opts.map((opt, oi) => (
                    <div key={opt.value || oi} style={{
                      display: 'flex', alignItems: 'center', gap: `${s(3, 3)}px`,
                      background: 'rgba(255,255,255,0.09)', border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: `${s(4, 4)}px`, padding: `${s(2, 2)}px ${s(5, 5)}px`,
                    }}>
                      <div style={{
                        width: `${s(6, 7)}px`, height: `${s(6, 7)}px`, borderRadius: '50%',
                        border: `1px solid ${accent}`, flexShrink: 0,
                      }} />
                      <span style={{ fontSize: `${s(7, 7)}px`, color: formText, whiteSpace: 'nowrap' }}>
                        {opt.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (field.type === 'checkbox') {
            return (
              <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: `${s(5, 5)}px` }}>
                <div style={{
                  width: `${s(10, 11)}px`, height: `${s(10, 11)}px`, flexShrink: 0,
                  border: '1px solid rgba(255,255,255,0.45)',
                  borderRadius: `${s(2, 2)}px`, background: 'rgba(255,255,255,0.08)',
                }} />
                <span style={{ fontSize: `${s(7, 7)}px`, color: `${formText}cc`, lineHeight: 1.3, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {field.label}
                </span>
              </div>
            );
          }

          if (field.type === 'select') {
            return (
              <div key={fi}>
                <div style={labelStyle}>{field.label}</div>
                <div style={{ ...inputBox, justifyContent: 'space-between' }}>
                  <span style={phStyle}>{field.placeholder || 'Select an option'}</span>
                  <span style={{ fontSize: `${s(7, 7)}px`, color: `${formText}55`, flexShrink: 0, marginLeft: `${s(3, 3)}px` }}>▾</span>
                </div>
              </div>
            );
          }

          if (field.type === 'textarea') {
            return (
              <div key={fi}>
                <div style={labelStyle}>{field.label}</div>
                <div style={{ ...inputBox, height: `${s(22, 26)}px`, alignItems: 'flex-start', paddingTop: `${s(4, 4)}px` }}>
                  <span style={phStyle}>{field.placeholder || '…'}</span>
                </div>
              </div>
            );
          }

          return (
            <div key={fi}>
              <div style={labelStyle}>{field.label}</div>
              <div style={inputBox}>
                <span style={phStyle}>
                  {field.placeholder || (field.type === 'email' ? 'email@example.com' : field.type === 'phone' ? '+1 (555) …' : 'Enter text…')}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MiniPagePreview({ background, layers }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.35);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setScale(w / EDITOR_W);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    for (const layer of layers || []) {
      if (layer.data?.type === 'text' && layer.data?.style?.fontFamily) {
        loadGoogleFont(layer.data.style.fontFamily);
      }
    }
  }, [layers]);

  return (
    <div ref={ref} className="w-full h-full overflow-hidden relative" style={bgToCss(background)}>
      {(layers || [])
        .filter(l => !l.hidden && l.data != null)
        .sort((a, b) => (a.transform.zIndex || 0) - (b.transform.zIndex || 0))
        .map(layer => {
          const id = layer._id || layer.id;
          const { x, y, width, height, rotation, opacity, zIndex } = layer.transform;
          const baseStyle = {
            position: 'absolute',
            left: `${x}%`,
            top: `${y}%`,
            width: `${width}%`,
            height: `${height}%`,
            opacity,
            zIndex,
            transform: rotation ? `rotate(${rotation}deg)` : undefined,
            overflow: 'hidden',
            pointerEvents: 'none',
          };
          const d = layer.data;

          if (d.type === 'text') {
            const s = d.style ?? {};
            const padding = s.padding || [0, 0, 0, 0];
            const content = typeof d.content === 'string' && !d.content.includes('[object') ? d.content : '';
            const justifyContent = s.textAlign === 'left' ? 'flex-start' : s.textAlign === 'right' ? 'flex-end' : 'center';
            const bgStyle = s.backgroundColor
              ? {
                  background: hexToRgba(s.backgroundColor, s.backgroundOpacity ?? 1),
                  padding: `${padding[0] * scale}px ${padding[1] * scale}px ${padding[2] * scale}px ${padding[3] * scale}px`,
                  borderRadius: (s.borderRadius ?? 0) * scale,
                  width: '100%',
                }
              : {
                  padding: `${padding[0] * scale}px ${padding[1] * scale}px ${padding[2] * scale}px ${padding[3] * scale}px`,
                  width: '100%',
                };
            const textStyle = {
              fontFamily: `'${s.fontFamily || 'sans-serif'}', sans-serif`,
              fontSize: (s.fontSize || 16) * scale,
              fontWeight: s.fontWeight || '400',
              lineHeight: s.lineHeight || 1.4,
              letterSpacing: (s.letterSpacing || 0) * scale,
              textAlign: s.textAlign || 'left',
              textTransform: s.textTransform || 'none',
              color: s.color || '#fff',
              textShadow: s.textShadow ?? undefined,
              WebkitTextStroke: s.stroke ? `${(s.strokeWidth || 0) * scale}px ${s.stroke}` : undefined,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
              padding: 0,
              width: '100%',
            };
            return (
              <div key={id} style={{ ...baseStyle, overflow: 'visible', display: 'flex', alignItems: 'center', justifyContent }}>
                <div style={bgStyle}>
                  <p style={textStyle}>{content}</p>
                </div>
              </div>
            );
          }

          if (d.type === 'shape') {
            return (
              <div key={id} style={baseStyle}>
                <ShapePreview data={d} />
              </div>
            );
          }

          if (d.type === 'image') {
            const focal = d.focalPoint;
            const pos = focal ? `${focal.x * 100}% ${focal.y * 100}%` : 'center';
            return (
              <img key={id} src={resolveMediaUrl(d.src)} alt=""
                style={{ ...baseStyle, objectFit: d.objectFit || 'cover', objectPosition: pos }} />
            );
          }

          if (d.type === 'video') {
            return <VideoPreview key={id} data={d} baseStyle={baseStyle} />;
          }

          if (d.type === 'overlay') {
            return <div key={id} style={{ ...baseStyle, background: d.fill }} />;
          }

          if (d.type === 'cta') {
            const cs = d.style ?? {};
            return (
              <div key={id} style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  background: cs.backgroundColor || 'var(--brand-primary)',
                  color: cs.color || '#fff',
                  fontSize: (cs.fontSize || 14) * scale,
                  fontWeight: cs.fontWeight || '700',
                  borderRadius: (cs.borderRadius || 50) * scale,
                  padding: `${4 * scale}px ${12 * scale}px`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}>
                  {d.text || 'Button'}
                </div>
              </div>
            );
          }

          if (d.type === 'icon') {
            return (
              <div key={id} style={{
                ...baseStyle,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: d.color || '#fff',
                fontSize: (d.fontSize || 48) * scale,
              }}>
                <i className={d.iconClass || ''} />
              </div>
            );
          }

          if (d.type === 'interactive') {
            const IMETA = {
              'binary-poll': { label: 'Poll',    emoji: '📊', color: 'var(--brand-400)' },
              'text-poll':   { label: 'Poll',    emoji: '📊', color: 'var(--brand-400)' },
              'image-poll':  { label: 'Poll',    emoji: '🖼️', color: '#a78bfa' },
              'text-quiz':   { label: 'Quiz',    emoji: '🧠', color: '#f472b6' },
              'image-quiz':  { label: 'Quiz',    emoji: '🧠', color: '#f472b6' },
              'slider':      { label: 'Slider',  emoji: '🎚️', color: 'var(--brand-400)' },
              'results':     { label: 'Results', emoji: '📈', color: '#34d399' },
            };
            const isDark = (d.theme || 'dark') === 'dark';
            const meta = IMETA[d.interactiveType] || { label: 'Interactive', emoji: '✨', color: 'var(--brand-400)' };
            const ibg      = isDark ? 'rgba(8,8,20,0.92)'     : 'rgba(255,255,255,0.96)';
            const itextCol = isDark ? '#ffffff'                : '#111827';
            const imuted   = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';
            const ioptBg   = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)';
            const ioptBord = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.14)';
            const itrackBg = isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)';
            const sc = scale;

            let iContent = null;
            if (d.interactiveType === 'binary-poll') {
              const opts = d.options || [];
              iContent = (
                <div style={{ display: 'flex', gap: `${4*sc}px` }}>
                  {[0,1].map(i => (
                    <div key={i} style={{ flex: 1, borderRadius: `${6*sc}px`, padding: `${6*sc}px ${3*sc}px`, background: ['var(--brand-primary)','#ec4899'][i], display: 'flex', flexDirection: 'column', alignItems: 'center', gap: `${2*sc}px` }}>
                      <span style={{ fontSize: `${11*sc}px`, lineHeight: 1 }}>{['👍','👎'][i]}</span>
                      <span style={{ fontSize: `${6*sc}px`, fontWeight: 700, color: '#fff', textAlign: 'center', lineHeight: 1.2 }}>{opts[i]?.text || ['Yes','No'][i]}</span>
                    </div>
                  ))}
                </div>
              );
            } else if (d.interactiveType === 'slider') {
              const min = d.sliderMin ?? 0, max = d.sliderMax ?? 100, val = d.sliderInitialValue ?? 50;
              const pct = ((val - min) / (max - min)) * 100;
              iContent = (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: `${5*sc}px` }}>
                    <span style={{ background: meta.color, color: '#fff', fontSize: `${8*sc}px`, fontWeight: 800, borderRadius: `${4*sc}px`, padding: `${1.5*sc}px ${7*sc}px` }}>{val}</span>
                  </div>
                  <div style={{ position: 'relative', height: `${4*sc}px`, background: itrackBg, borderRadius: `${2*sc}px`, margin: `0 ${5*sc}px` }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: meta.color, borderRadius: `${2*sc}px` }} />
                    <div style={{ position: 'absolute', top: '50%', left: `${pct}%`, transform: 'translate(-50%,-50%)', width: `${10*sc}px`, height: `${10*sc}px`, borderRadius: '50%', background: meta.color, border: `${1.5*sc}px solid #fff` }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: `${3*sc}px`, padding: `0 ${3*sc}px`, fontSize: `${5.5*sc}px`, color: imuted }}>
                    <span>{min}</span><span>{max}</span>
                  </div>
                </div>
              );
            } else if (d.interactiveType === 'results') {
              const cats = (d.resultsCategories || []).slice(0, 3);
              iContent = (
                <div style={{ display: 'flex', flexDirection: 'column', gap: `${3*sc}px` }}>
                  {cats.map((cat, i) => (
                    <div key={cat.id || i} style={{ display: 'flex', alignItems: 'center', gap: `${4*sc}px` }}>
                      <div style={{ width: `${6*sc}px`, height: `${6*sc}px`, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontSize: `${6*sc}px`, fontWeight: 700, color: itextCol, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.title}</div>
                        {cat.description && <div style={{ fontSize: `${5*sc}px`, color: imuted, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.description}</div>}
                      </div>
                      <span style={{ fontSize: `${5*sc}px`, color: meta.color, fontWeight: 700, flexShrink: 0 }}>≥{cat.threshold}%</span>
                    </div>
                  ))}
                </div>
              );
            } else {
              const opts = (d.options || []).slice(0, 4);
              const isQuiz = d.interactiveType?.includes('quiz');
              const letters = ['A','B','C','D'];
              iContent = (
                <div style={{ display: 'flex', flexDirection: 'column', gap: `${3*sc}px` }}>
                  {opts.map((o, i) => {
                    const correct = isQuiz && o.isCorrect;
                    return (
                      <div key={o.id || i} style={{ background: correct ? 'rgba(34,197,94,0.20)' : ioptBg, border: `1px solid ${correct ? '#4ade80' : ioptBord}`, borderRadius: `${5*sc}px`, padding: `${4*sc}px ${5*sc}px`, display: 'flex', alignItems: 'center', gap: `${4*sc}px` }}>
                        {isQuiz && <span style={{ width: `${11*sc}px`, height: `${11*sc}px`, borderRadius: `${3*sc}px`, flexShrink: 0, background: correct ? '#22c55e' : 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: `${5.5*sc}px`, fontWeight: 800, color: '#fff' }}>{letters[i]}</span>}
                        <span style={{ flex: 1, fontSize: `${6*sc}px`, fontWeight: 500, color: itextCol, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.text || `Option ${i+1}`}</span>
                        {correct && <span style={{ fontSize: `${6*sc}px`, color: '#4ade80' }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              );
            }

            return (
              <div key={id} style={{ ...baseStyle, background: ibg, borderRadius: `${8*sc}px`, padding: `${6*sc}px`, display: 'flex', flexDirection: 'column', gap: `${4*sc}px`, border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.10)'}`, boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: `${3*sc}px` }}>
                  <span style={{ fontSize: `${9*sc}px`, lineHeight: 1 }}>{meta.emoji}</span>
                  <span style={{ background: meta.color, color: '#fff', fontSize: `${5.5*sc}px`, fontWeight: 700, textTransform: 'uppercase', padding: `${1*sc}px ${4*sc}px`, borderRadius: `${10*sc}px`, letterSpacing: '0.07em' }}>{meta.label}</span>
                </div>
                {d.prompt && <p style={{ margin: 0, fontSize: `${7*sc}px`, fontWeight: 700, color: itextCol, lineHeight: 1.35 }}>{d.prompt}</p>}
                {iContent}
              </div>
            );
          }

          if (d.type === 'form') {
            return (
              <div key={id} style={baseStyle}>
                <FormPreview data={d} scale={scale} />
              </div>
            );
          }

          return null;
        })
      }
    </div>
  );
}

function VideoPreview({ data, baseStyle }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    el.play().catch(() => {});
  }, [data.src]);
  return (
    <video
      ref={ref}
      src={resolveMediaUrl(data.src)}
      poster={data.poster ? resolveMediaUrl(data.poster) : undefined}
      style={{ ...baseStyle, objectFit: 'cover' }}
      autoPlay
      loop={data.loop ?? true}
      muted
      playsInline
    />
  );
}
